import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'

import Column from '@amzn/meridian/column'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'
import Button from '@amzn/meridian/button'
import Divider from '@amzn/meridian/divider'
import { useBundle } from '@amzn/react-arb-tools'

import { getSelectedTool } from '@atredux/selectors/toolSelector'
import { getStoreId } from '@atredux/selectors/storeSelector'
import { getNotificationSheetState } from '@atredux/selectors/notificationSelector'
import { getSelectedToolLaunchStoreId, isSelectedToolViewOnly } from '@atredux/selectors/viewOnlySelector'
import { getAssociateEntryTool } from '@services/ToolMenuConfigService'
import { useViewOnlySelector } from '@hooks/useViewOnlySelector'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'

import './StoreScanRequiredModal.scss'

export const REQUIRED_TITLE = 'jsma-web-storeScan-requiredTitle'
export const REQUIRED_MESSAGE = 'jsma-web-storeScan-requiredMessage'
export const GO_TO_ASSOCIATE_ENTRY = 'jsma-web-storeScan-goToAssociateEntry'

/**
 * Blocks non-exempt tools until the associate has scanned into a store.
 *
 * A tool registered viewOnlySupported is not blocked once it has a store to launch against:
 * the shell opens it with viewOnly=true and the tool renders read-only, carrying its own
 * "scan into the store" messaging. With no store at all (nothing scanned or selected) it is
 * still blocked, since the tool has no store to show.
 */
const StoreScanRequiredModal = (): React.ReactElement | null => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const selectedTool = useSelector(getSelectedTool)
    const currentStoreId = useSelector(getStoreId)
    const launchStoreId = useViewOnlySelector(getSelectedToolLaunchStoreId)
    const isViewOnly = useViewOnlySelector(isSelectedToolViewOnly)
    const isNotificationsOpen = useSelector(getNotificationSheetState)

    const isExemptFromStoreScan = selectedTool?.skipStoreScan === true
    const hasNeverScanned = !currentStoreId || currentStoreId === 'NO_CURRENT_STORE'
    const opensViewOnly = isViewOnly && !!launchStoreId

    let open = false
    if (hasNeverScanned) {
        // User has never scanned — block non-exempt tools, but allow notifications
        open = !isExemptFromStoreScan && !opensViewOnly && !isNotificationsOpen
    }

    const handleGoToAssociateEntry = useCallback(() => {
        const tool = getAssociateEntryTool()
        if (tool) {
            _navigateToTool(tool)
        }
    }, [])

    // TODO: "Back to previous store" feature removed — revisit or delete permanently.
    // https://app.asana.com/1/8442528107068/project/1210908264295868/task/1213891602140731

    if (!open) {
        return null
    }

    return (
        <div className={`store-scan-required-overlay ${isNotificationsOpen ? 'notifications-open' : ''}`}>
            <div className="store-scan-required-content">
                <Column spacing="400">
                    <Text type="h100">
                        {t(bundle, REQUIRED_TITLE)}
                    </Text>
                    <Text>
                        {t(bundle, REQUIRED_MESSAGE)}
                    </Text>
                    <Divider />
                    <Row alignmentHorizontal="center" className="primary-theme-button">
                        <Button type="primary" minWidth="100%" onClick={handleGoToAssociateEntry}>
                            {t(bundle, GO_TO_ASSOCIATE_ENTRY)}
                        </Button>
                    </Row>
                </Column>
            </div>
        </div>
    )
}

export default StoreScanRequiredModal
